import { booleanCol, enumCol, stringCol } from '#core/lib/expr/schema/types.ts';

import type { VirtualView } from '../createReshapedView.ts';
import { createStaticView } from '../createStaticView.ts';

/**
 * The seven IPSO scenario constraints, keyed by a stable constraint key with
 * its human-readable label. The single source of truth the three constraint
 * tables draw their rows from.
 */
export const SCENARIO_CONSTRAINTS = {
  fieldCapacity: 'Field Capacity (max rate)',
  availableGasLiftRate: 'Available Gas Lift Rate',
  reservoirManagement: 'Reservoir Management Guidelines',
  facilityDowntime: 'Facility planned downtime (at actuals)',
  minFbhp: 'Flowing Bottomhole Pressure (min)',
  maxWellRate: 'Maximum well rate',
  wellControlRanges: 'Well Control ranges (Choke size, gas-lift rate, Pump speed)',
} as const;

/** A constraint key from the shared {@link SCENARIO_CONSTRAINTS} set. */
export type ScenarioConstraintKey = keyof typeof SCENARIO_CONSTRAINTS;

/**
 * One IPSO scenario constraint table: its metadata values, the subset of
 * constraint keys applied for the scenario, the scenario-specific
 * Maximum-well-rate label, and the on-demand progressive-disclosure
 * description.
 */
export type ScenarioConstraintTable = {
  readonly prefix:
    | 'hydraulic'
    | 'technical'
    | 'fsopr'
    | 'adHocSimulation1'
    | 'adHocSimulation2'
    | 'adHocOptimization1'
    | 'adHocOptimization2'
    | 'systemOptimized'
    | 'systemOptimizedChoke';
  displayName: string;
  readonly applied: readonly ScenarioConstraintKey[];
  readonly description: string;
};

/**
 * The three IPSO scenario constraint tables, each expressed once against the
 * shared constraint and metadata sources of truth. Hydraulic → table 8-4,
 * Technical → table 8-5, FSOPR → table 8-6.
 */
export const SCENARIO_CONSTRAINT_TABLES: readonly ScenarioConstraintTable[] = [
  {
    prefix: 'hydraulic',
    applied: ['minFbhp', 'wellControlRanges'],
    displayName: 'Hydraulic Rate',
    description:
      'Calculates the Field Capacity Rate by recommending optimum well operating controls based on flowing bottomhole pressure and well control constraints.',
  },
  {
    prefix: 'technical',
    applied: ['fieldCapacity', 'availableGasLiftRate', 'minFbhp', 'wellControlRanges'],
    displayName: 'Technical Rate',
    description: 'Calculates the Field Technical Rate based on current real-time well and network conditions.',
  },
  {
    prefix: 'fsopr',
    displayName: 'FSOPR Rate',
    applied: [
      'fieldCapacity',
      'availableGasLiftRate',
      'reservoirManagement',
      'facilityDowntime',
      'minFbhp',
      'maxWellRate',
      'wellControlRanges',
    ],
    description:
      'Calculates the Field Sustainable Oil Production Rate (FSOPR) based on a 12-month rolling forecast, considering field conditions, reservoir rate targets, and network capacity.',
  },
  {
    prefix: 'adHocSimulation1',
    displayName: 'Ad-hoc Production Scenario 1',
    applied: [],
    description: '(n/a)',
  },
  {
    prefix: 'adHocSimulation2',
    displayName: 'Ad-hoc Production Scenario 2',
    applied: [],
    description:
      'Simulates Field Production Capacity/Potential with all wells available for production operating within Rate Calculation Guidelines',
  },

  {
    prefix: 'adHocOptimization1',
    displayName: 'Ad-hoc Optimization Scenario 1',
    applied: [],
    description: 'Evaluates field production impact by shutting in all wells with water cut at or above 50%.',
  },
  {
    prefix: 'adHocOptimization2',
    displayName: 'Ad-hoc Optimization Scenario 2',
    applied: [],
    description: '(n/a)',
  },
  {
    prefix: 'systemOptimized',
    displayName: 'Real-Time Production Optimization (Artificial Lift)',
    applied: [
      'fieldCapacity',
      'availableGasLiftRate',
      'reservoirManagement',
      'facilityDowntime',
      'minFbhp',
      'maxWellRate',
      'wellControlRanges',
    ],
    description:
      'Recommends Gas Lift rate and ESP frequency changes across wells to improve field production based on real-time field conditions.',
  },
  {
    prefix: 'systemOptimizedChoke',
    displayName: 'Real-Time Production Optimization (Choke)',
    applied: [
      'fieldCapacity',
      'availableGasLiftRate',
      'reservoirManagement',
      'facilityDowntime',
      'minFbhp',
      'maxWellRate',
      'wellControlRanges',
    ],
    description:
      'Recommends choke bean size adjustments across wells to maximize field production based on current operating conditions.',
  },
];

const CONSTRAINT_KEYS = Object.keys(SCENARIO_CONSTRAINTS) as readonly ScenarioConstraintKey[];

/** One projected row of the ScenarioConstraints static view. */
export type ScenarioConstraintRow = {
  readonly scenario: string;
  readonly constraint: string;
  readonly constraintLabel: string;
  readonly applied: boolean;
  readonly description: string;
};

/** One row per (scenario, constraint) for every scenario that has a table. */
const constraintRows: readonly ScenarioConstraintRow[] = SCENARIO_CONSTRAINT_TABLES.filter(
  (it) => it.applied.length > 0,
).flatMap((table: ScenarioConstraintTable) =>
  CONSTRAINT_KEYS.map((key: ScenarioConstraintKey) => ({
    scenario: table.prefix,
    constraint: key,
    constraintLabel: SCENARIO_CONSTRAINTS[key],
    applied: table.applied.includes(key),
    description: table.description,
  })),
);

/**
 * The full projected row set of the ScenarioConstraints view: one row per
 * (scenario, constraint) for the three scenarios that have a table, followed by
 * one descriptive row per table-less scenario.
 */
export const SCENARIO_CONSTRAINT_ROWS: readonly ScenarioConstraintRow[] = constraintRows;

/**
 * Expose the IPSO scenario constraint reference tables as a single static
 * virtual view: one row per (scenario, constraint) for the three scenarios that
 * have a table, plus one descriptive row per table-less scenario so any
 * scenario query returns a meaningful answer.
 */
export const scenarioConstraintViews = (): readonly VirtualView[] => [
  createStaticView({
    name: 'ScenarioConstraints',
    description: `Reference table of optimization scenario constraints.

## Scenario descriptions

Quote relevant scenario descriptions verbatim, to ensure the constraints are clearly communicated in context.

When displaying scenario descriptions in a dashboard: Render the Markdown panel with the descriptions to the left of the constraints table in the dashboard.

NOTE: Omit constraints table for ad-hoc scenarios, but *always* include the scenario description in the dashboard.

<scenario_descriptions>
${SCENARIO_CONSTRAINT_TABLES.map((table) => `Scenario description for the "${table.displayName}" scenario (prefix=\`${table.prefix}\`): "${table.description}`).join('\n')}"
</scenario_descriptions>
`,
    columns: [
      {
        name: 'scenario',
        type: stringCol,
        description:
          'The optimization-scenario prefix: hydraulic | technical | fsopr | systemOptimized | systemOptimizedChoke',
      },
      {
        name: 'constraint',
        type: enumCol(CONSTRAINT_KEYS),
        description: 'The constraint key for this row',
      },
      { name: 'constraintLabel', type: stringCol, description: 'The human-readable label of the constraint.' },
      { name: 'applied', type: booleanCol, description: 'Whether the constraint is applied for the scenario.' },
      // {
      //   name: 'description',
      //   type: stringCol,
      //   description: 'The on-demand progressive-disclosure description for the scenario.',
      // },
    ],
    rows: SCENARIO_CONSTRAINT_ROWS,
  }),
];
